import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Nova Pulse — a periodic AoE burst centered on the player. Implemented as
 * stationary, single-hit, very-short-lived pooled projectiles sized to the
 * pulse radius rather than a special-cased instant check, so it goes
 * through the same collision/damage-multiplier pipeline as every other
 * weapon instead of needing its own. Rewards staying close to the boss —
 * the opposite risk/reward of the long-range weapons.
 *
 * Every cast fires two rings on the same frame: a fast, small "inner
 * shock" and a slower, wider "outer wave". Both spawn simultaneously, but
 * giving them different `lifetime`s makes the renderer's existing
 * lifetime-ratio ring-expansion animation play them as a visible
 * crack-then-boom pair for free.
 *
 * At level 3+ that main burst is followed by a smaller single-ring echo a
 * beat later, and at level 6+ a third, weaker echo follows that — each
 * queued as a seconds-remaining entry (not the base fire-rate timer) so
 * every layer centers on wherever the player actually is when it lands,
 * not a stale snapshot. At level 8 (capstone) the cast also throws out 4
 * "detonator" motes that spiral outward around the player using
 * Projectile's existing orbit fields, each popping into its own small
 * shockwave when it expires — a distinct, sweeping silhouette rather than
 * a bigger version of Orbit Ward's static lingering zone.
 */
export class NovaPulse extends Weapon {
  constructor() {
    super(WEAPONS.novaPulse);
    this._pendingEchoes = []; // { timer, mult }
    this._detonators = []; // { proj, timer }
    this._detonatorSpin = 0;
  }

  update(dt, origin, target, pool) {
    super.update(dt, origin, target, pool);
    for (let i = this._pendingEchoes.length - 1; i >= 0; i--) {
      this._pendingEchoes[i].timer -= dt;
      if (this._pendingEchoes[i].timer <= 0) {
        const { radiusScale, mult } = this._pendingEchoes[i];
        this._pendingEchoes.splice(i, 1);
        this._fireEcho(origin, pool, radiusScale, mult);
      }
    }
    for (let i = this._detonators.length - 1; i >= 0; i--) {
      const entry = this._detonators[i];
      entry.proj.orbitRadius += 90 * dt;
      entry.timer -= dt;
      if (entry.timer <= 0) {
        entry.proj.active = false;
        this._detonators.splice(i, 1);
        pool.fire({
          x: entry.proj.x,
          y: entry.proj.y,
          radius: 34 + this.level * 1.5,
          damage: Math.round(this._mainDamage() * 0.4),
          hits: 1,
          lifetime: 0.15,
          color: this.config.color,
          style: 'ring',
        });
      }
    }
  }

  fire(origin, _target, pool) {
    this._fireInnerShock(origin, pool);
    this._fireOuterWave(origin, pool);
    this.lastBurstRadius = this._outerRadius();
    if (this.level >= 3) this._pendingEchoes.push({ timer: 0.3, radiusScale: 0.75, mult: 0.55 });
    if (this.level >= 6) this._pendingEchoes.push({ timer: 0.55, radiusScale: 0.55, mult: 0.35 });
    if (this.level >= 8) this._fireDetonationRing(origin, pool);
  }

  _outerRadius() {
    return this.config.radius + this.level * 2;
  }

  // Combined damage of the inner shock + outer wave — the reference number
  // echoes/detonators scale off of.
  _mainDamage() {
    return (6 + this.level * 1.6) + (8 + this.level * 1.4);
  }

  _fireInnerShock(origin, pool) {
    pool.fire({
      x: origin.x,
      y: origin.y,
      radius: 26 + this.level * 1.2,
      damage: 6 + this.level * 1.6,
      hits: 1,
      lifetime: 0.12,
      color: this.config.color,
      style: 'ring',
    });
  }

  _fireOuterWave(origin, pool) {
    pool.fire({
      x: origin.x,
      y: origin.y,
      radius: this._outerRadius(),
      damage: 8 + this.level * 1.4,
      hits: 1,
      lifetime: 0.22,
      color: this.config.color,
      style: 'ring',
    });
  }

  _fireEcho(origin, pool, radiusScale, damageMultiplier) {
    pool.fire({
      x: origin.x,
      y: origin.y,
      radius: this._outerRadius() * radiusScale,
      damage: this._mainDamage() * damageMultiplier,
      hits: 1,
      lifetime: 0.16,
      color: this.config.color,
      style: 'ring',
    });
  }

  _fireDetonationRing(origin, pool) {
    for (let i = 0; i < 4; i++) {
      const proj = pool.fire({
        x: origin.x,
        y: origin.y,
        radius: 6,
        damage: Math.round(this._mainDamage() * 0.2),
        hits: 1e9,
        hitCooldownDuration: 0.2,
        lifetime: 0.6,
        color: this.config.color,
        orbitCenter: origin,
        orbitRadius: 20,
        // Offset a little each cast so four consecutive detonation rings
        // don't all sweep the exact same four spots — purely cosmetic.
        orbitAngle: i * (Math.PI / 2) + this._detonatorSpin,
        orbitAngularSpeed: 6,
      });
      this._detonators.push({ proj, timer: 0.6 });
    }
    this._detonatorSpin += 0.35;
  }
}
